import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Decode HTML entities if content appears to be escaped
 * This handles cases where HTML might be stored as &lt;table&gt; instead of <table>
 * Works in both browser and server environments
 */
function decodeHTMLEntities(content: string): string {
  if (!content) return content;
  
  // Check if content has HTML entities that should be decoded
  // If we see &lt; or &gt; but no actual HTML tags, it's likely escaped
  const hasEscapedHTML = /&lt;[a-z]/i.test(content) && !/<[a-z]/i.test(content);
  
  if (hasEscapedHTML) {
    // Decode common HTML entities
    // This works in both browser and server environments
    return content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  }
  
  return content;
}

/**
 * Fix incomplete HTML table structures
 * Wraps orphaned <tr> and <td> elements in proper <table> structure
 */
function fixIncompleteTables(content: string): string {
  if (!content) return content;
  
  // Check if we have table rows/cells without a table wrapper
  const hasTableRows = /<tr[\s\S]*?<\/tr>/i.test(content);
  const hasTableCells = /<td[\s\S]*?<\/td>/i.test(content) || /<th[\s\S]*?<\/th>/i.test(content);
  const hasTableWrapper = /<table[\s\S]*?<\/table>/i.test(content);
  
  // If we have table rows/cells but no table wrapper, wrap them
  if ((hasTableRows || hasTableCells) && !hasTableWrapper) {
    // Use a simpler regex approach: find blocks of consecutive table elements
    // Match one or more complete table rows (tr with content and closing tag)
    const tableRowBlockRegex = /((?:<tr[\s\S]*?<\/tr>[\s\n]*)+)/gi;
    
    return content.replace(tableRowBlockRegex, (match) => {
      // Check if this match is already inside a table tag
      // We do this by checking the context before the match
      const matchIndex = content.indexOf(match);
      const beforeMatch = content.substring(0, matchIndex);
      
      // Count unclosed table tags before this match
      const openTables = (beforeMatch.match(/<table[^>]*>/gi) || []).length;
      const closeTables = (beforeMatch.match(/<\/table>/gi) || []).length;
      const isInsideTable = openTables > closeTables;
      
      if (isInsideTable) {
        // Already inside a table, don't wrap
        return match;
      }
      
      // Wrap in proper table structure
      return `<table>\n<tbody>\n${match.trim()}\n</tbody>\n</table>`;
    });
  }
  
  return content;
}

/**
 * Preprocess markdown content to convert custom directives to HTML
 * Converts `::: @iframe URL :::` syntax to HTML iframe elements
 * Also preserves single line breaks by converting them to markdown hard breaks
 */
function preprocessMarkdown(content: string): string {
  if (!content) return content;
  
  // Preserve single line breaks by converting them to markdown hard breaks (two spaces + newline)
  // ReactMarkdown collapses single \n, so we need to convert single line breaks
  // to markdown hard line breaks (two spaces + newline) or double line breaks (paragraph breaks)
  // We'll convert single line breaks that aren't already part of a double break
  // to use markdown hard line breaks (two spaces + newline)
  let processed = content
    // First, protect existing double line breaks
    .replace(/\n\n/g, '___DOUBLE_BREAK___')
    // Convert remaining single line breaks to markdown hard breaks (two spaces + newline)
    .replace(/\n/g, '  \n')
    // Restore double line breaks
    .replace(/___DOUBLE_BREAK___/g, '\n\n');
  
  // Convert ::: @iframe URL ::: syntax to HTML iframe
  // Matches patterns like:
  // ::: @iframe https://www.youtube.com/embed/kDFEn4kpbGE
  // :::
  // Or with markdown link format:
  // ::: @iframe [https://www.youtube.com/embed/kDFEn4kpbGE](https://www.youtube.com/embed/kDFEn4kpbGE)
  // :::
  // Handles both single-line and multi-line formats
  // The regex uses [\s\S] to match any character including newlines between @iframe and closing :::
  const iframeRegex = /:::[\s]*@iframe[\s]+([\s\S]*?)[\s]*:::/g;
  
  return processed.replace(iframeRegex, (match, urlContent) => {
    // Extract URL from the content between @iframe and :::
    let url = urlContent.trim();
    
    // Check if it's in markdown link format: [text](url)
    const markdownLinkMatch = url.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (markdownLinkMatch) {
      url = markdownLinkMatch[2]; // Extract URL from markdown link
    }
    
    // Clean up the URL - remove any remaining brackets or whitespace
    const cleanUrl = url.replace(/^\[|\]$/g, '').trim();
    
    // Validate URL (basic check)
    if (!cleanUrl || (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://') && !cleanUrl.startsWith('//'))) {
      // If we can't extract a valid URL, return the original match
      console.warn('MarkdownRenderer: Could not extract valid URL from iframe directive:', match);
      return match;
    }
    
    // Return HTML iframe with responsive styling
    // Removed overflow: hidden from outer div to prevent content cutoff
    return `<div class="iframe-container" style="position: relative; padding-bottom: 56.25%; height: 0; max-width: 100%; margin: 1.5rem 0; border-radius: 0.5rem;">
      <iframe 
        src="${cleanUrl}" 
        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;" 
        allowfullscreen
        loading="lazy"
        title="Embedded content"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      ></iframe>
    </div>`;
  });
}

export default function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  if (!content) {
    return null;
  }
  
  // Decode HTML entities if content is escaped
  // This handles cases where HTML might be stored as &lt;table&gt; instead of <table>
  const decodedContent = decodeHTMLEntities(content);
  
  // Fix incomplete HTML table structures (wrap orphaned tr/td in table tags)
  const fixedTables = fixIncompleteTables(decodedContent);
  
  // Preprocess content to handle custom directives (like @iframe)
  const processedContent = preprocessMarkdown(fixedTables);
  
  // Check if content contains HTML tags (like tables, divs, etc.)
  // This helps us determine if we should render as HTML or markdown
  const hasHTMLTags = /<[a-z][\s\S]*>/i.test(processedContent);
  
  // Specifically check for table tags
  const hasTableTags = /<table[\s\S]*?<\/table>/i.test(processedContent) || 
                       /<tr[\s\S]*?<\/tr>/i.test(processedContent) ||
                       /<td[\s\S]*?<\/td>/i.test(processedContent) ||
                       /<th[\s\S]*?<\/th>/i.test(processedContent);
  
  // Check if content has markdown syntax (bold, headers, lists, etc.)
  const hasMarkdownSyntax = /(\*\*|__|##|###|\* |\- |\d+\. |`|\[.*\]\(.*\))/.test(processedContent);
  
  // Check if content is pure HTML (starts with HTML tag and has no markdown)
  const isPureHTML = processedContent.trim().startsWith('<') && !hasMarkdownSyntax;
  
  // Always use ReactMarkdown with rehypeRaw for mixed content (markdown + HTML)
  // This ensures proper rendering of both markdown tables and HTML tables
  // rehypeRaw allows HTML to pass through, and remarkGfm handles markdown tables
  return (
    <div className={`prose prose-lg max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeHighlight]}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
